// src/components/Pages/ResellSchedulingPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Group,
    Table,
    Select,
    Checkbox,
    ActionIcon,
    TextInput,
    Textarea,
    Modal,
    Button,
    Tooltip,
    Pagination,
    Center,
    Switch,
    Badge,
    CloseButton
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
    IconCalendarTime,
    IconWorld,
    IconLanguage,
    IconRadar2,
    IconCheck,
    IconExternalLink,
    IconTrash,
    IconCalendar,
    IconClock,
    IconFileText,
    IconRefresh,
    IconCalendarCheck,
    IconSearch,
    IconArrowBackUp,
    IconPencil
} from '@tabler/icons-react';
import { useShallow } from 'zustand/shallow';
import { notifications } from '@mantine/notifications';
import copy from 'copy-to-clipboard';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useResellPipelineStore, { selectSchedulingItems } from '../../stores/resellPipelineStore';

// ----------------- Utils -----------------
const CMS_RESELL_URL_PRELIVE_TEST = 'https://controlcentre.loveincorporated.com/msnglobal/Content/Resell/Create/';
const CMS_EDIT_URL_BASE = 'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';

const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date?.toDate === 'function') return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d) ? null : d;
    }
    return null;
};

const toISODateString = (date) => {
    const d = normalizeDate(date);
    return d ? d.toLocaleDateString('sv-SE') : null;
};

// New short date formatter for the "Added" column
const toShortDate = (date) => {
    const d = normalizeDate(date);
    if (!d) return '-';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const formatGroupHeaderDate = (date) => {
    const d = normalizeDate(date);
    if (!d) return 'Unscheduled / Inbox';
    return d.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const calculateDaysResting = (date) => {
    const lastDate = normalizeDate(date);
    if (!lastDate) return 'N/A';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfLastDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
    const diff = startOfToday - startOfLastDate;
    return Math.floor(diff / (1000 * 3600 * 24));
};

const calculatePVsPerVar = (pageViews, cdsEntries) => {
    const views = parseInt(pageViews, 10);
    const entries = parseInt(cdsEntries, 10);
    
    if (isNaN(views) || isNaN(entries) || entries === 0) return 'NA';
    
    const result = Math.round(views / entries);
    return result.toLocaleString('en-US'); 
};

const validateAndFormatTime = (timeStr) => {
    if (!timeStr) return { isValid: false, error: 'Empty' };
    const parts = timeStr.split(':');
    if (parts.length === 1 && /^\d{1,2}$/.test(parts[0])) {
        const h = +parts[0];
        if (h >= 0 && h <= 23) return { isValid: true, formattedTime: `${parts[0].padStart(2, '0')}:00` };
    }
    if (parts.length === 2) {
        const [hs, ms] = parts;
        if (!/^\d{1,2}$/.test(hs) || !/^\d{1,2}$/.test(ms)) return { isValid: false, error: 'Invalid' };
        const h = +hs, m = +ms;
        if (h < 0 || h > 23 || m < 0 || m > 59) return { isValid: false, error: 'Invalid' };
        return { isValid: true, formattedTime: `${hs.padStart(2, '0')}:${ms.padStart(2, '0')}` };
    }
    return { isValid: false, error: 'Format HH:MM' };
};

const sanitizeForTSV = (value) => {
    if (value === null || typeof value === 'undefined') return '';
    return String(value).replace(/\t/g, ' ').replace(/(\r\n|\n|\r)/g, ' ');
};

const formatItemsForExcel = (items) => {
    const headers = [
        'Date',
        'New Title',
        'Canonical ID',
        'Type',
        'Old Title',
        'Send Time',
        'New Image Slide',
        'Brand',
        'Last Entry',
        'Last Feed ID',
        'PVs per Var' // <-- NEW
    ];
    let tsv = headers.join('\t') + '\n';
    items.forEach(item => {
        const row = [
            item.pubDate ? toISODateString(item.pubDate) : '',
            item.newTitle,
            item.originalId,
            item.type,
            item.latestTitle,
            item.pubTime,
            item.newLeadImageSlideNo,
            item.Brand,
            item.lastEntryDate ? toISODateString(item.lastEntryDate) : '',
            item.lastFeedId,
            calculatePVsPerVar(item.pageViews, item.cdsEntries) // <-- NEW
        ];
        tsv += row.map(sanitizeForTSV).join('\t') + '\n';
    });
    return tsv;
};

const getResellUrl = (item, feedsMap) => {
    const baseUrl = `${CMS_RESELL_URL_PRELIVE_TEST}${item.lastFeedId}`;
    const params = [];

    if (item.newTitle) params.push(`newTitle=${encodeURIComponent(item.newTitle)}`);
    if (item.newLeadImageSlideNo) params.push(`selectedSlide=${item.newLeadImageSlideNo}`);
    const d = normalizeDate(item.pubDate);
    if (d) {
        const [hours = '00', minutes = '00'] = (item.pubTime || '00:00').split(':');
        d.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const h = d.getHours().toString().padStart(2, '0');
        const m = d.getMinutes().toString().padStart(2, '0');
        params.push(`publishDate=${year}-${month}-${day}T${h}:${m}:00`);
    }

    const feedKey = `${item.Brand || ''}_${item.language || ''}_${item.region || ''}`;
    const feedId = feedsMap[feedKey];
    if (feedId) params.push(`feedId=${feedId}`);
    params.push('goliveimmediately=true');

    return `${baseUrl}?${params.join('&')}`;
};

// --- Audit Logger (Fire & Forget) ---
const logTitleAuditEvent = (eventData) => {
    try {
        const db = getFirestore();
        addDoc(collection(db, 'audit_title_changes'), {
            ...eventData,
            timestamp: serverTimestamp()
        }).catch(err => console.error("Telemetry failed silently:", err));
    } catch (e) {
        console.error("Telemetry construction failed:", e);
    }
};

// 3. UPDATE SchedulingRow Component
const SchedulingRow = React.memo(function SchedulingRow({
    item,
    isDark,
    theme,
    selectedIds,
    feedsMap,
    hasManagementRole,
    handleRowSelect,
    handleFieldUpdate,
    handleTimeChange,
    onToggleCMS,
    onReject,
    onEditTitle,
    onEditSlide,
    onComplete,
    onDelete
}) {
    const isSelected = selectedIds.includes(item.id);
    const pubDateValue = normalizeDate(item.pubDate);
    const isScheduled = !!pubDateValue;

    const resellUrl = useMemo(() => getResellUrl(item, feedsMap), [item, feedsMap]);

    const [localHours, setLocalHours] = useState('');
    const [localMinutes, setLocalMinutes] = useState('');

    useEffect(() => {
        const [h, m] = (item.pubTime || ':').split(':');
        setLocalHours(h || '');
        setLocalMinutes(m || '');
    }, [item.pubTime]);

    const handleSelect = useCallback(() => handleRowSelect(item.id), [item.id, handleRowSelect]);
    const onDateChange = useCallback((d) => handleFieldUpdate(item.id, 'pubDate', d), [item.id, handleFieldUpdate]);

    const getCMSTooltip = () => {
        if (!isScheduled) return "Set a date to enable this flag";
        if (item.isCMSSetup) return "CMS Live/Queued (Click to undo)";
        return "Mark as 'Created'/'Set Live' in CMS";
    };

    return (
        <Table.Tr
            key={item.id}
            style={{
                verticalAlign: 'middle',
                backgroundColor: isSelected ? (isDark ? theme.colors.dark[5] : theme.colors.gray[1]) : undefined
            }}
        >
            <Table.Td>
                <Checkbox checked={isSelected} onChange={handleSelect} />
            </Table.Td>

            {/* Title & Info Columns... */}
            {/* 1. New Title (With Management Edit) */}
            <Table.Td>
                <Group gap={6} wrap="nowrap" align="center">
                    <Box style={{ flex: 1 }}>
                        <Text size="sm" fw={500} lineClamp={2} title={item.newTitle}>{item.newTitle || 'Untitled'}</Text>
                        <Text size="xs" c="dimmed">OLD: {item.latestTitle}</Text>
                    </Box>
                    {hasManagementRole && (
                        <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="xs"
                            onClick={() => onEditTitle(item)}
                            style={{ opacity: 0.5 }} // Subtle until hovered
                        >
                            <IconPencil size={12} />
                        </ActionIcon>
                    )}
                </Group>
            </Table.Td>

            {/* 2. Slide (Click-to-Edit for Management) */}
            <Table.Td ta="center">
                <Badge
                    variant="light"
                    color="gray"
                    size="sm"
                    onClick={hasManagementRole ? () => onEditSlide(item) : undefined}
                    style={{
                        cursor: hasManagementRole ? 'pointer' : 'default',
                        transition: 'opacity 0.2s'
                    }}
                    // Add a subtle hover effect for Management to indicate interactivity
                    onMouseEnter={(e) => { if (hasManagementRole) e.currentTarget.style.opacity = 0.7; }}
                    onMouseLeave={(e) => { if (hasManagementRole) e.currentTarget.style.opacity = 1; }}
                >
                    {item.newLeadImageSlideNo || '#'}
                </Badge>
            </Table.Td>
            <Table.Td>{item.Brand}</Table.Td>
            <Table.Td ta="center">
                <Text size="xs" c="dimmed">{toShortDate(item.addedAt)}</Text>
            </Table.Td>
            <Table.Td ta="center">{calculateDaysResting(item.lastEntryDate)}</Table.Td>
            <Table.Td ta="center">{item.originalId}</Table.Td>
            <Table.Td ta="center">{item.cdsEntries ?? 'N/A'}</Table.Td>
            <Table.Td ta="center"> 
                <Text size="xs" fw={500}>{calculatePVsPerVar(item.pageViews, item.cdsEntries)}</Text>
            </Table.Td>
            <Table.Td ta="center">
                {item.lastFeedId && (
                    <Tooltip label={`Edit Latest Variant: ${item.lastFeedId}`} withArrow>
                        <Button component="a" href={`${CMS_EDIT_URL_BASE}${item.lastFeedId}`} target="_blank" rel="noopener noreferrer" size="compact-xs" variant="outline" style={{ minWidth: 70, justifyContent: 'center' }}>
                            {item.lastFeedId}
                        </Button>
                    </Tooltip>
                )}
            </Table.Td>

            <Table.Td>
                <DatePickerInput
                    placeholder="Pick date"
                    value={pubDateValue || null}
                    onChange={onDateChange}
                    size="xs"
                    clearable
                    style={{ minWidth: 130 }}
                />
            </Table.Td>

            <Table.Td>
                <Group gap={4} wrap="nowrap" justify="center">
                    <TextInput placeholder="HH" value={localHours} onChange={(e) => setLocalHours(e.currentTarget.value)} onBlur={() => handleTimeChange(item.id, 'hours', localHours)} size="xs" maxLength={2} styles={{ input: { width: 36, textAlign: 'center', padding: 0 } }} />
                    <Text>:</Text>
                    <TextInput placeholder="MM" value={localMinutes} onChange={(e) => setLocalMinutes(e.currentTarget.value)} onBlur={() => handleTimeChange(item.id, 'minutes', localMinutes)} size="xs" maxLength={2} styles={{ input: { width: 36, textAlign: 'center', padding: 0 } }} />
                </Group>
            </Table.Td>

            <Table.Td ta="center">
                {item.lastFeedId ? (
                    <Button component="a" href={resellUrl} target="_blank" rel="noopener noreferrer" size="compact-sm" variant="filled" color="grape" rightSection={<IconExternalLink size={14} />}>
                        Resell
                    </Button>
                ) : (<Text size="xs" c="red">No Feed ID</Text>)}
            </Table.Td>

            <Table.Td>
                <Group gap={8} justify="center" wrap="nowrap">

                    {/* CMS Setup Toggle */}
                    <Tooltip label={getCMSTooltip()} withArrow>
                        <span style={{ marginRight: 6, marginTop: 4 }}>
                            <ActionIcon
                                variant={item.isCMSSetup ? "filled" : "default"}
                                color={item.isCMSSetup ? "teal" : "gray"}
                                size="sm"
                                disabled={!isScheduled}
                                onClick={() => isScheduled && onToggleCMS(item.id, item.isCMSSetup)}
                            >
                                <IconCalendarCheck size={16} />
                            </ActionIcon>
                        </span>
                    </Tooltip>
                    <Tooltip label="Reject: Push back to Preparation" withArrow>
                        <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={() => onReject(item)}
                            style={{ marginRight: 4 }}
                        >
                            <IconArrowBackUp size={16} />
                        </ActionIcon>
                    </Tooltip>
                    {/* Management Actions */}
                    {hasManagementRole && (
                        <>
                            <Tooltip label="Mark Complete (Removes from pipeline)" withArrow>
                                <ActionIcon variant="filled" color="green" size="sm" onClick={() => onComplete(item)}>
                                    <IconCheck size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete" withArrow>
                                <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onDelete(item)}>
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Tooltip>

                        </>
                    )}
                </Group>
            </Table.Td>
        </Table.Tr>
    );
});

// ----------------- Main Page Component -----------------
function ResellSchedulingPage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userProfile, currentUser } = useAuth();

    // --- Role Check ---
    const hasManagementRole = useMemo(() =>
        userProfile && ['admin', 'management'].includes(userProfile.role),
        [userProfile]
    );

    // --- Store Access ---
    const {
        isLoading,
        region, language, brand,
        feedsMap,
        setRegion, setLanguage, setBrand,
        initializeSubscriptions, cleanup,
        updateField,
        bulkUpdate,
        deleteItem,
        toggleCMSSetup,
        rejectToPreparation
    } = useResellPipelineStore(useShallow(state => ({
        isLoading: state.isLoading,
        region: state.region,
        language: state.language,
        brand: state.brand,
        feedsMap: state.feedsMap,
        setRegion: state.setRegion,
        setLanguage: state.setLanguage,
        setBrand: state.setBrand,
        initializeSubscriptions: state.initializeSubscriptions,
        cleanup: state.cleanup,
        updateField: state.updateField,
        bulkUpdate: state.bulkUpdate,
        deleteItem: state.deleteItem,
        toggleCMSSetup: state.toggleCMSSetup,
        rejectToPreparation: state.rejectToPreparation
    })));
    const schedulingItems = useResellPipelineStore(selectSchedulingItems);

    // --- Local State ---
    const [selectedIds, setSelectedIds] = useState([]);
    const [activePage, setPage] = useState(1);
    const ROWS_PER_PAGE = 50;

    // Toggles
    const [isGroupedView, setIsGroupedView] = useState(false);
    const [showUnscheduledOnly, setShowUnscheduledOnly] = useState(false);
    const [showScheduledOnly, setShowScheduledOnly] = useState(false);

    // NEW: Search Query State
    const [searchQuery, setSearchQuery] = useState('');

    // Bulk Input State
    const [bulkDate, setBulkDate] = useState(null);
    const [bulkTime, setBulkTime] = useState('');
    const [bulkTimeError, setBulkTimeError] = useState(null);

    // Excel State
    const [isExcelCopied, setIsExcelCopied] = useState(false);

    // Modals
    const [itemToComplete, setItemToComplete] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isBulkCompleteOpen, setBulkCompleteOpen] = useState(false);
    const [itemToReject, setItemToReject] = useState(null);
    const [editTitleItem, setEditTitleItem] = useState(null); // { id, val }
    const [editSlideItem, setEditSlideItem] = useState(null); // { id, val }
    // --- Lifecycle ---
    useEffect(() => {
        initializeSubscriptions();
        return () => cleanup();
    }, []);
    useEffect(() => {
        const r = searchParams.get('region');
        const l = searchParams.get('language');
        const b = searchParams.get('brand');
        if (r) setRegion(r);
        if (l) setLanguage(l);
        if (b) setBrand(b);
    }, [searchParams]);

    // --- FILTERING & SORTING LOGIC (UPDATED) ---
    const filteredItems = useMemo(() => {
        let items = [...schedulingItems];

        // 0. Search Filter (Original ID or Last Feed ID)
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            items = items.filter(i =>
                (i.originalId && String(i.originalId).toLowerCase().includes(q)) ||
                (i.lastFeedId && String(i.lastFeedId).toLowerCase().includes(q))
            );
        }

        // 1. Unscheduled Only (Inbox)
        if (showUnscheduledOnly) {
            items = items.filter(i => !i.pubDate);
        }
        // 2. Scheduled Only (Calendar)
        else if (showScheduledOnly) {
            items = items.filter(i => i.pubDate);
        }

        // 3. Sorting Logic
        return items.sort((a, b) => {
            const dateA = normalizeDate(a.pubDate);
            const dateB = normalizeDate(b.pubDate);

            // A. Separate Scheduled vs Unscheduled
            if (!dateA && dateB) return -1; // A is unscheduled (Inbox), A comes first
            if (dateA && !dateB) return 1; // B is unscheduled, B comes first

            // B. If BOTH are Unscheduled (Inbox) -> Sort by AddedAt (Newest First)
            if (!dateA && !dateB) {
                const addedA = normalizeDate(a.addedAt) || new Date(0);
                const addedB = normalizeDate(b.addedAt) || new Date(0);
                return addedB - addedA; // Descending
            }

            // C. If BOTH are Scheduled -> Sort by Schedule Date (Asc)
            const dateDiff = dateA - dateB;
            if (dateDiff !== 0) return dateDiff;

            // Tie-breaker 1: Time (Asc)
            const timeA = a.pubTime || '00:00';
            const timeB = b.pubTime || '00:00';
            const timeDiff = timeA.localeCompare(timeB);
            if (timeDiff !== 0) return timeDiff;

            // Tie-breaker 2: AddedAt (Desc)
            const addedA = normalizeDate(a.addedAt) || new Date(0);
            const addedB = normalizeDate(b.addedAt) || new Date(0);
            return addedB - addedA;
        });

    }, [schedulingItems, showUnscheduledOnly, showScheduledOnly, searchQuery]);

    // Handle Toggle interactions
    const toggleUnscheduled = (checked) => {
        setShowUnscheduledOnly(checked);
        if (checked) setShowScheduledOnly(false);
    };

    const toggleScheduled = (checked) => {
        setShowScheduledOnly(checked);
        if (checked) setShowUnscheduledOnly(false);
    };

    // --- Grouping Logic ---
    const groupedItems = useMemo(() => {
        if (!isGroupedView) return null;
        return filteredItems.reduce((acc, item) => {
            const key = formatGroupHeaderDate(item.pubDate);
            (acc[key] ||= []).push(item);
            return acc;
        }, {});
    }, [filteredItems, isGroupedView]);

    useEffect(() => {
        setSelectedIds([]);
        setPage(1);
    }, [filteredItems.length, isGroupedView]);

    // --- Pagination / Rendering Logic ---
    const totalPages = Math.ceil(filteredItems.length / ROWS_PER_PAGE);
    const itemsToRender = useMemo(() => {
        if (isGroupedView) return groupedItems;
        const start = (activePage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return filteredItems.slice(start, end);
    }, [isGroupedView, groupedItems, filteredItems, activePage]);

    const itemIdsForSelection = useMemo(() => {
        if (isGroupedView) {
            return Object.values(groupedItems).flat().map(i => i.id);
        }
        return itemsToRender.map(i => i.id);
    }, [isGroupedView, groupedItems, itemsToRender]);

    const handleRowSelect = useCallback((id) => {
        setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }, []);

    const handleSelectAll = () => {
        const pageIds = itemIdsForSelection;
        const allSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
        }
    };

    const allOnPageSelected = itemIdsForSelection.length > 0 && itemIdsForSelection.every(id => selectedIds.includes(id));
    const someOnPageSelected = itemIdsForSelection.some(id => selectedIds.includes(id)) && !allOnPageSelected;

    // --- Actions ---
    const handleTimeChange = useCallback((id, segment, value) => {
        const item = schedulingItems.find(it => it.id === id);
        const curTime = item?.pubTime || ':';
        let [h, m] = curTime.split(':');
        if (segment === 'hours') h = String(value).padStart(2, '0');
        else m = String(value).padStart(2, '0');
        updateField(id, 'pubTime', `${h || '00'}:${m || '00'}`);
    }, [schedulingItems, updateField]);

    const handleConfirmBulkUpdate = (field, value) => {
        if (selectedIds.length > 0 && value !== null) {
            bulkUpdate(selectedIds, field, value);
            setSelectedIds([]);
            if (field === 'pubDate') setBulkDate(null);
            if (field === 'pubTime') setBulkTime('');
        }
    };

    const handleConfirmComplete = () => {
        if (itemToComplete) {
            // Pass 'true' to suppress the generic "Item Deleted" message
            deleteItem(itemToComplete.id, true);
            setItemToComplete(null);
            // Keep this specific message
            notifications.show({ title: 'Completed', message: 'Item marked as complete.', color: 'green' });
        }
    };

    const handleConfirmDelete = () => {
        if (itemToDelete) {
            deleteItem(itemToDelete.id);
            setItemToDelete(null);
        }
    };

    const handleConfirmReject = () => {
        if (itemToReject) {
            rejectToPreparation(itemToReject.id); // Call the store action
            setItemToReject(null); // Close modal
        }
    };

    // Updated Handlers
    const handleSaveTitle = (id, newValue) => {
        // 1. Locate the item to evaluate the delta
        const item = schedulingItems.find(i => i.id === id);

        if (item) {
            const oldVal = item.newTitle || '';

            // 2. Only fire telemetry if the string has actually mutated
            if (newValue !== oldVal) {
                logTitleAuditEvent({
                    userUid: currentUser?.uid || 'unknown',
                    userEmail: currentUser?.email || 'unknown',
                    feedId: item.lastFeedId || item['Last Feed ID'],
                    originalId: item.originalId,
                    brand: item.Brand || 'Unknown',
                    region: item.region || region || 'Unknown',
                    prior_newTitle: oldVal,
                    newTitle: newValue,
                    source: 'scheduling_manual_edit',
                    statusTarget: 'Scheduling'
                });
            }
        }

        // 3. Proceed with standard UI updates
        updateField(id, 'newTitle', newValue);
        setEditTitleItem(null);
        notifications.show({ title: 'Updated', message: 'Title updated.', color: 'green' });
    };

    const handleSaveSlide = (id, newValue) => {
        const num = parseInt(newValue, 10);
        updateField(id, 'newLeadImageSlideNo', isNaN(num) ? null : num);
        setEditSlideItem(null);
        notifications.show({ title: 'Updated', message: 'Slide number updated.', color: 'green' });
    };

    const handleBulkComplete = () => {
        selectedIds.forEach(id => deleteItem(id));
        setSelectedIds([]);
        setBulkCompleteOpen(false);
        notifications.show({ title: 'Bulk Complete', message: `${selectedIds.length} items completed.`, color: 'green' });
    };

    // Excel Copy
    const handleCopyToExcel = () => {
        const tsv = formatItemsForExcel(filteredItems);
        try {
            copy(tsv, { format: 'text/plain' });
            setIsExcelCopied(true);
            setTimeout(() => setIsExcelCopied(false), 2000);
            notifications.show({
                title: 'Copied',
                message: `${filteredItems.length} items ready for Excel.`,
                color: 'green'
            });
        } catch (err) {
            console.error("Copy failed", err);
            notifications.show({ title: 'Copy Failed', message: 'Could not copy data.', color: 'red' });
        }
    };

    // Shared row props
    const rowProps = {
        isDark, theme, selectedIds, feedsMap,
        hasManagementRole,
        handleRowSelect, handleFieldUpdate: updateField, handleTimeChange,
        onToggleCMS: toggleCMSSetup,
        onReject: setItemToReject,
        onEditTitle: (item) => setEditTitleItem({ id: item.id, val: item.newTitle || '' }),
        onEditSlide: (item) => setEditSlideItem({ id: item.id, val: item.newLeadImageSlideNo || '' }),
        onComplete: setItemToComplete, onDelete: setItemToDelete
    };

    return (
        <Box style={{
            width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column',
            backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
            backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'scroll',
        }}>
            {/* --- SECTION 1: HEADER & FILTERS --- */}
            <Container size="xl" py="xl">
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconCalendarTime size={32} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    Resell Scheduling
                </Title>
                <Text size="lg" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Set dates and push final candidates to the CMS.
                </Text>

                <Paper withBorder shadow="sm" p="md" radius="md" mb="md">
                    <Group grow mb="md">
                        <Select
                            label="Region"
                            leftSection={<IconWorld size={16} />}
                            data={['UK', 'US', 'AE', 'AU', 'CA', 'DE', 'ES', 'FR', 'ZA']}
                            value={region}
                            onChange={(v) => setRegion(v || 'US')}
                        />
                        <Select
                            label="Language"
                            leftSection={<IconLanguage size={16} />}
                            data={['EN', 'DE', 'ES', 'FR']}
                            value={language}
                            onChange={(v) => setLanguage(v || 'EN')}
                        />
                        <Select
                            label="Brand"
                            leftSection={<IconRadar2 size={16} />}
                            data={['All', 'loveMONEY', 'loveFOOD', 'loveEXPLORING', 'lovePROPERTY']}
                            value={brand}
                            onChange={(v) => setBrand(v || 'All')}
                        />
                        {/* UPDATED: Search Input inserted into the main filter row */}
                        {/* UPDATED: Search Input with Clear Button */}
                        <TextInput
                            label="Search IDs"
                            placeholder="Original ID / Feed ID"
                            leftSection={<IconSearch size={16} />}
                            rightSection={
                                searchQuery.length > 0 ? (
                                    <CloseButton
                                        aria-label="Clear search"
                                        onClick={() => setSearchQuery('')}
                                        style={{ cursor: 'pointer' }}
                                    />
                                ) : null
                            }
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.currentTarget.value)}
                        />
                    </Group>

                    {/* Center Toggles & Right Buttons */}
                    <Center>
                        <Group gap="xl">
                            <Switch
                                label="Group by Date"
                                checked={isGroupedView}
                                onChange={(e) => setIsGroupedView(e.currentTarget.checked)}
                            />
                            <Switch
                                label="Unscheduled Only"
                                checked={showUnscheduledOnly}
                                onChange={(e) => toggleUnscheduled(e.currentTarget.checked)}
                            />
                            <Switch
                                label="Scheduled Only"
                                checked={showScheduledOnly}
                                onChange={(e) => toggleScheduled(e.currentTarget.checked)}
                            />
                        </Group>
                    </Center>

                    {/* Management Actions (Bottom Right) */}
                    {hasManagementRole && (
                        <Group justify="flex-end" mt="md">
                            <Button
                                variant="outline"
                                color={isExcelCopied ? 'teal' : 'green'}
                                onClick={handleCopyToExcel}
                                leftSection={isExcelCopied ? <IconRefresh size={16} /> : <IconFileText size={16} />}
                                size="xs"
                            >
                                {isExcelCopied ? 'Copied!' : 'Copy View for Excel'}
                            </Button>
                            <Button
                                variant="outline"
                                size="xs"
                                onClick={() => navigate(`/resell-radar?region=${region}&language=${language}&brand=${brand}`)}
                            >
                                Go to Resell Radar
                            </Button>
                        </Group>
                    )}
                </Paper>
            </Container>

            {/* --- SECTION 2: BULK ACTIONS --- */}
            {selectedIds.length > 0 && (
                <Container size="xl" pb="md">
                    <Paper withBorder shadow="sm" p="sm" radius="md" mb="md" style={{ borderColor: theme.colors.blue[5] }}>
                        <Group justify="space-between" align="center">
                            <Text size="sm" fw={500}>{selectedIds.length} item(s) selected</Text>

                            <Group>
                                <Group gap={4}>
                                    <DatePickerInput
                                        placeholder="Set Date"
                                        leftSection={<IconCalendar size={14} />}
                                        value={bulkDate}
                                        onChange={setBulkDate}
                                        size="xs"
                                        style={{ width: 130 }}
                                    />
                                    <Button size="xs" onClick={() => handleConfirmBulkUpdate('pubDate', bulkDate)} disabled={!bulkDate}>
                                        Set
                                    </Button>
                                </Group>

                                <Group gap={4}>
                                    <TextInput
                                        placeholder="HH:MM"
                                        leftSection={<IconClock size={14} />}
                                        value={bulkTime}
                                        onChange={(e) => {
                                            const val = e.currentTarget.value;
                                            setBulkTime(val);
                                            const check = validateAndFormatTime(val);
                                            setBulkTimeError(check.isValid || val === '' ? null : 'Invalid');
                                        }}
                                        size="xs"
                                        error={!!bulkTimeError}
                                        style={{ width: 90 }}
                                    />
                                    <Button
                                        size="xs"
                                        onClick={() => {
                                            const check = validateAndFormatTime(bulkTime);
                                            if (check.isValid) handleConfirmBulkUpdate('pubTime', check.formattedTime);
                                        }}
                                        disabled={!bulkTime || !!bulkTimeError}
                                    >
                                        Set
                                    </Button>
                                </Group>

                                <Button
                                    size="xs"
                                    color="green"
                                    onClick={() => setBulkCompleteOpen(true)}
                                    leftSection={<IconCheck size={16} />}
                                    style={{ marginLeft: 16 }}
                                >
                                    Mark Complete
                                </Button>
                            </Group>
                        </Group>
                    </Paper>
                </Container>
            )}

            {/* --- SECTION 3: TABLE --- */}
            <Container fluid px="xl" pb="xl">
                <Paper withBorder shadow="sm" radius="md" p="md">
                    {isLoading ? (
                        <Center p="xl"><Loader /><Text ml="md">Loading...</Text></Center>
                    ) : filteredItems.length === 0 ? (
                        <Center p="xl"><Text c="dimmed">No scheduling items found.</Text></Center>
                    ) : (
                        <>
                            <Table.ScrollContainer minWidth={1600}>
                                <Table striped highlightOnHover verticalSpacing="sm">
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th w={40} style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>
                                                <Checkbox
                                                    checked={allOnPageSelected}
                                                    indeterminate={someOnPageSelected}
                                                    onChange={handleSelectAll}
                                                />
                                            </Table.Th>
                                            <Table.Th>New Title</Table.Th>
                                            <Table.Th w={80} ta="center">Slide</Table.Th>
                                            <Table.Th w={100}>Brand</Table.Th>
                                            <Table.Th c="dimmed" w={80} ta="center">Added</Table.Th>
                                            <Table.Th w={80} ta="center">Resting</Table.Th>
                                            <Table.Th w={100} ta="center">Original ID</Table.Th>
                                            <Table.Th w={80} ta="center">Variants</Table.Th>
                                            <Table.Th w={90} ta="center">PVs / Var</Table.Th>
                                            <Table.Th w={120} ta="center">Last Variant</Table.Th>
                                            <Table.Th w={220}>Send Date</Table.Th>
                                            <Table.Th w={120} ta="center">Send Time</Table.Th>
                                            <Table.Th w={120} ta="center">CMS</Table.Th>
                                            <Table.Th w={120} ta="center">Actions</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>

                                    <Table.Tbody>
                                        {isGroupedView ? (
                                            Object.keys(itemsToRender).map((dateKey) => (
                                                <React.Fragment key={dateKey}>
                                                    <Table.Tr style={{
                                                        backgroundColor: isDark ? theme.colors.blue[9] : theme.colors.blue[0],
                                                        position: 'sticky', top: 38, zIndex: 1
                                                    }}>
                                                        <Table.Td colSpan={13} fw={700} c={isDark ? 'white' : 'blue'}>
                                                            {dateKey}
                                                        </Table.Td>
                                                    </Table.Tr>
                                                    {itemsToRender[dateKey].map((item) => (
                                                        <SchedulingRow key={item.id} item={item} {...rowProps} />
                                                    ))}
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            itemsToRender.map(item => (
                                                <SchedulingRow key={item.id} item={item} {...rowProps} />
                                            ))
                                        )}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                            {totalPages > 1 && !isGroupedView && (
                                <Group justify="center" mt="xl">
                                    <Pagination total={totalPages} value={activePage} onChange={setPage} />
                                </Group>
                            )}
                        </>
                    )}
                </Paper>
            </Container>

            {/* --- MODALS --- */}
            <Modal opened={!!itemToComplete} onClose={() => setItemToComplete(null)} title="Confirm Completion" centered>
                <Text size="sm" mb="md">
                    Have you successfully created the resell entry in the CMS?
                </Text>
                <Text size="xs" c="dimmed" mb="md" fs="italic">
                    Ref: {itemToComplete?.newTitle || itemToComplete?.latestTitle}
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setItemToComplete(null)}>No, Cancel</Button>
                    <Button color="green" onClick={handleConfirmComplete}>Yes, Complete</Button>
                </Group>
            </Modal>

            <Modal opened={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Confirm Deletion" centered>
                <Text size="sm" mb="md" c="red" fw={500}>
                    Are you sure you want to DELETE this item?
                </Text>
                <Text size="sm" mb="md">
                    This will remove it from the pipeline entirely.
                    It will NOT be marked as completed.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setItemToDelete(null)}>Cancel</Button>
                    <Button color="red" onClick={handleConfirmDelete}>Delete Forever</Button>
                </Group>
            </Modal>

            {/* Reject / Return to Prep Modal */}
            <Modal opened={!!itemToReject} onClose={() => setItemToReject(null)} title="Confirm Rejection" centered>
                <Text size="sm" mb="md">
                    Are you sure you want to push "<b>{itemToReject?.newTitle || itemToReject?.latestTitle}</b>" back to the Preparation stage?
                </Text>
                <Text size="xs" c="dimmed" mb="md">
                    This will clear the scheduled date and set the status back to 'Staging'.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setItemToReject(null)}>Cancel</Button>
                    <Button color="orange" onClick={handleConfirmReject}>Confirm Reject</Button>
                </Group>
            </Modal>

            <Modal opened={isBulkCompleteOpen} onClose={() => setBulkCompleteOpen(false)} title="Bulk Completion" centered>
                <Text size="sm">
                    Are you sure you want to mark {selectedIds.length} items as complete?
                </Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setBulkCompleteOpen(false)}>Cancel</Button>
                    <Button color="green" onClick={handleBulkComplete}>Confirm Bulk Complete</Button>
                </Group>
            </Modal>

            {/* Optimized Edit Title Modal */}
            <EditTitleModal
                item={editTitleItem}
                onClose={() => setEditTitleItem(null)}
                onSave={handleSaveTitle}
            />

            {/* Optimized Edit Slide Modal */}
            <EditSlideModal
                item={editSlideItem}
                onClose={() => setEditSlideItem(null)}
                onSave={handleSaveSlide}
            />
        </Box>
    );
}

// --- OPTIMIZED MODAL COMPONENTS (Prevents Table Re-renders) ---

const EditTitleModal = ({ item, onClose, onSave }) => {
    // Local state handles keystrokes immediately
    const [localValue, setLocalValue] = useState(item?.val || '');

    // Sync local state when the item opens
    useEffect(() => {
        setLocalValue(item?.val || '');
    }, [item]);

    if (!item) return null;

    return (
        <Modal opened={true} onClose={onClose} title="Edit Title" centered>
            <Textarea
                label="New Title"
                placeholder="Enter new title..."
                minRows={3}
                value={localValue}
                onChange={(e) => setLocalValue(e.currentTarget.value)}
                data-autofocus
            />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={onClose}>Cancel</Button>
                <Button color="blue" onClick={() => onSave(item.id, localValue)}>Save Title</Button>
            </Group>
        </Modal>

    );
};

const EditSlideModal = ({ item, onClose, onSave }) => {
    const [localValue, setLocalValue] = useState(item?.val || '');

    useEffect(() => {
        setLocalValue(item?.val || '');
    }, [item]);

    if (!item) return null;

    return (
        <Modal opened={true} onClose={onClose} title="Edit Slide Number" centered size="sm">
            <TextInput
                label="Lead Image Slide"
                placeholder="#"
                type="number"
                value={localValue}
                onChange={(e) => setLocalValue(e.currentTarget.value)}
                data-autofocus
            />
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={onClose}>Cancel</Button>
                <Button color="blue" onClick={() => onSave(item.id, localValue)}>Save Slide</Button>
            </Group>
        </Modal>
    );
};

export default ResellSchedulingPage;